/** Card 2 — 30-second caregiver wellbeing check. Anonymous. */
import React, { useState } from "react";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { TransitionPanel, DemoBadge, OptionButton, trackStep, trackCompleted } from "../activationShared";

const QUESTIONS = [
    { q: "Have you slept — a real night's sleep — in the last few days?", yesIsGood: true },
    { q: "Have you left the house this week for something that wasn't an errand or appointment?", yesIsGood: true },
    { q: "Do you feel overwhelmed most days?", yesIsGood: false },
    { q: "Do you have help — someone who shares the load?", yesIsGood: true },
    { q: "Have you taken even 30 minutes for yourself this week?", yesIsGood: true },
];

const TIERS = [
    { max: 1, title: "You're holding steady", body: "You're carrying real responsibility and still finding moments for yourself. That balance is worth protecting — most caregivers lose it slowly, without noticing." },
    { max: 3, title: "You're carrying a lot", body: "A few warning lights are on. Caregiver strain builds quietly — poor sleep, isolation, and no relief hours compound into burnout. This is exactly the time to put support in place, before it's urgent." },
    { max: 5, title: "You're running on empty", body: "What you're describing is how burnout looks from the inside: no sleep, no breaks, no backup. You are not failing — you're under-supported. Respite isn't a luxury; it's how caregivers keep going." },
];

export default function WellbeingCheck({ onClose }) {
    const [answers, setAnswers] = useState([]);
    const idx = answers.length;
    const done = idx >= QUESTIONS.length;
    const concern = answers.filter((a, i) => (QUESTIONS[i].yesIsGood ? a === 0 : a === 1)).length;
    const tier = TIERS.find((t) => concern <= t.max) || TIERS[2];

    const pick = (v) => {
        const next = [...answers, v];
        setAnswers(next);
        trackStep("wellbeing_check", `q${idx + 1}`);
        if (next.length >= QUESTIONS.length) trackCompleted("wellbeing_check");
    };

    return (
        <div data-testid="activation-wellbeing">
            <DemoBadge>30-second check — anonymous, nothing is saved</DemoBadge>
            {!done ? (
                <>
                    <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#6E7A74]">{idx + 1} of {QUESTIONS.length}</p>
                    <h3 className="mt-1 font-heading font-medium text-lg md:text-xl text-[#1F2624]">{QUESTIONS[idx].q}</h3>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                        <OptionButton onClick={() => pick(0)} testid={`wellbeing-q${idx + 1}-no`}>No</OptionButton>
                        <OptionButton onClick={() => pick(1)} testid={`wellbeing-q${idx + 1}-yes`}>Yes</OptionButton>
                    </div>
                </>
            ) : (
                <div data-testid="wellbeing-result">
                    <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-coral-dark">Your wellness summary</p>
                    <h3 className="mt-1 font-heading font-semibold text-2xl text-[#12776F]">{tier.title}</h3>
                    <p className="mt-3 text-sm text-[#2A3330] leading-relaxed">{tier.body}</p>
                    <p className="mt-4 text-sm font-semibold text-[#1F2624]">Support that exists right now:</p>
                    <ul className="mt-2 space-y-2 text-sm text-[#4C5551]">
                        <li>
                            <a href="https://www.alz.org/help-support/resources/helpline" target="_blank" rel="noopener noreferrer" className="text-teal-dark font-medium hover:underline inline-flex items-center gap-1">
                                Alzheimer&apos;s Association 24/7 Helpline <ExternalLink size={12} />
                            </a>{" "}
                            — 1-800-272-3900, free, staffed day and night.
                        </li>
                        <li>
                            <Link to="/resources?category=guide" className="text-teal-dark font-medium hover:underline">Medicare&apos;s GUIDE program</Link>{" "}
                            — includes an annual respite benefit so you can take a real break.
                        </li>
                        <li>
                            Local examples: your Area Agency on Aging (eldercare.acl.gov), adult day programs, and faith-community respite volunteers.
                        </li>
                    </ul>
                    <TransitionPanel card="wellbeing_check" onClose={onClose} ctaLabel="Create your free account to keep tracking your wellbeing" />
                </div>
            )}
        </div>
    );
}
